import React from 'react';
import { motion } from 'motion/react';
import { ShoppingBag, Minus, Plus, Ticket, Trash2, ChevronRight } from 'lucide-react';
import { formatPrice } from '../data/products';
import { AppliedVoucherStatus } from './AppliedVoucherStatus';
import { OrderProductCard } from './OrderProductCard';

export const EmptyCart = ({ onContinueShopping }: { onContinueShopping: () => void }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8">
    <div className="w-20 h-20 bg-ink/[0.03] flex items-center justify-center mb-6">
      <ShoppingBag className="w-8 h-8 text-ink/20" />
    </div>
    <h3 className="text-2xl font-serif text-ink mb-3 tracking-tight">Keranjang Anda Kosong</h3>
    <p className="text-sm text-muted/60 mb-8 leading-relaxed">
      Koleksi kami menunggu untuk menjadi bagian dari cerita gaya Anda.
    </p>
    <button
      onClick={onContinueShopping}
      className="text-[10px] font-bold text-ink uppercase tracking-[0.2em] border-b border-ink/20 pb-1 hover:border-brand-wine hover:text-brand-wine transition-all"
    >
      Mulai Menjelajah
    </button>
  </div>
);

export const CartItem = ({
  item,
  updateQuantity,
  removeItem
}: {
  item: any;
  updateQuantity: (sku: string, qty: number) => void;
  removeItem: (sku: string) => void;
}) => (
  <OrderProductCard
    item={item}
    variant="cart"
    onUpdateQuantity={updateQuantity}
    onRemove={removeItem}
  />
);

export const VoucherSelector = ({ 
  appliedVoucher, 
  onOpen 
}: { 
  appliedVoucher: any; 
  onOpen: () => void 
}) => (
  <div 
    onClick={onOpen}
    className="mb-4 py-3 px-4 border border-ink/5 bg-paper flex items-center justify-between cursor-pointer hover:bg-ink/[0.02] transition-all group"
  >
    <div className="flex items-center gap-3">
      <Ticket size={16} className="text-brand-wine" />
      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-ink">
        {appliedVoucher ? 'Ganti Voucher' : 'Gunakan Voucher'}
      </span>
    </div>
    <ChevronRight size={14} className="text-muted/30 group-hover:translate-x-0.5 transition-transform" />
  </div>
);

export const AppliedVoucher = ({
  appliedVoucher,
  isMinPurchaseMet,
  discount,
  subtotal,
  removeVoucher
}: {
  appliedVoucher: any;
  isMinPurchaseMet: boolean;
  discount: number;
  subtotal: number;
  removeVoucher: () => void;
}) => (
  <AppliedVoucherStatus
    voucher={appliedVoucher}
    isMinPurchaseMet={isMinPurchaseMet}
    discount={discount}
    subtotal={subtotal}
    onRemove={removeVoucher}
    className="mb-6"
  />
);

export const CartSummary = ({
  subtotal,
  discount,
  finalTotal
}: {
  subtotal: number;
  discount: number;
  finalTotal: number;
}) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center group">
      <span className="text-[10px] font-bold text-muted/60 uppercase tracking-[0.2em]">Subtotal</span>
      <span className={`text-sm transition-colors ${discount > 0 ? 'text-muted/40 line-through' : 'font-serif text-lg text-ink'}`}>
        {formatPrice(subtotal)}
      </span>
    </div>

    {discount > 0 && (
      <div className="flex justify-between items-center animate-in fade-in slide-in-from-right-4 duration-500">
        <span className="text-[10px] font-bold text-brand-wine uppercase tracking-[0.2em]">Potongan Voucher</span>
        <span className="text-lg font-serif text-brand-wine">
          -{formatPrice(discount)}
        </span>
      </div>
    )}

    <div className="pt-4 border-t border-ink/5 flex justify-between items-center">
      <span className="text-[11px] font-black text-ink uppercase tracking-[0.2em]">Total</span>
      <span className="text-2xl font-serif text-brand-wine tracking-tight">
        {formatPrice(finalTotal)}
      </span>
    </div>
  </div>
);
